import Papa from 'papaparse';
import BillUploadConfig from '../features/BillUpload/interfaces/BillUploadConfig';
import moment from 'moment';

const formatParseErrors = (errors: Papa.ParseError[]): string =>
  errors
    .slice(0, 3)
    .map((e) => e.message)
    .join('; ');

export const getCsvHeaders = (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    let resolved = false;

    Papa.parse(file, {
      skipEmptyLines: true,
      delimiter: ',',
      step(results, parser) {
        if (results.errors.length) {
          parser.abort();
          reject(new Error(`Could not read "${file.name}": ${formatParseErrors(results.errors)}`));
          return;
        }
        resolved = true;
        resolve(results.data as string[]);
        parser.abort();
      },
      complete() {
        if (!resolved) {
          reject(new Error(`"${file.name}" appears to be empty. Please upload a CSV with at least a header row.`));
        }
      },
      error(error) {
        reject(new Error(`Failed to read "${file.name}": ${error.message}`));
      },
    });
  });
};

export const getCsvData = (file: File): Promise<string[][]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      skipEmptyLines: true,
      delimiter: ',',
      complete(result) {
        if (result.errors.length) {
          console.warn(`Parse warnings in "${file.name}":`, formatParseErrors(result.errors));
        }

        const filteredData = (result.data as string[][]).filter((row) =>
          row.some((value) => value !== '')
        );

        resolve(filteredData);
      },
      error(error) {
        reject(new Error(`Failed to parse "${file.name}": ${error.message}`));
      },
    });
  });
};

const checkMissingHeaders = (
  csvHeaders: string[],
  requiredHeaders: string[],
  fileName: string
): void => {
  const csvHeadersSet = new Set(csvHeaders);
  const missing = requiredHeaders.filter((h) => !csvHeadersSet.has(h));

  if (missing.length > 0) {
    throw new Error(
      `"${missing.join("', '")}" are missing in ${fileName}`
    );
  }
};

const checkHasData = (csvData: string[][], file: File): void => {
  const dataRows = csvData.slice(1);
  if (dataRows.length === 0) {
    throw new Error(
      `"${file.name}" contains only the header row — no data to upload. Please add at least one data row.`
    );
  }
};

export const isValidCsv = (
  requiredHeaders: string[],
  file: File,
  billUploadConfig: BillUploadConfig
): Promise<true> => {
  return getCsvHeaders(file)
    .then(async (csvHeaders) => {
      checkMissingHeaders(csvHeaders, requiredHeaders, file.name);

      const billDateCol = billUploadConfig.columns.find((item) => item.to === 'billDate');
      if (!billDateCol) {
        throw new Error('Bill upload configuration is missing the "Bill Date" column mapping. Please contact support.');
      }
      const dateFormat: string | undefined = billDateCol.dataConversionOptions?.dateFormat;
      const dateColName: string = billDateCol.from;

      const csvData = await getCsvData(file);
      checkHasData(csvData, file);

      const billIndex = csvHeaders.indexOf(dateColName);
      if (billIndex === -1) {
        throw new Error(`Could not locate the "Bill Date" column ("${dateColName}") in "${file.name}".`);
      }

      const normalizedFormat = dateFormat
        ?.replace(/yyyy/g, 'YYYY')
        .replace(/yy/g, 'YY')
        .replace(/dd/g, 'DD');

      if (dateFormat && normalizedFormat) {
        csvData.forEach((row, rowIndex) => {
          if (rowIndex === 0) return;
          if (row.length <= 1) return;

          const cellValue = row[billIndex];
          const isValidDate = moment(cellValue, normalizedFormat, true).isValid();

          if (!isValidDate) {
            throw new Error(
              `Bill Date format "${dateFormat}" is not matched`
            );
          }
        });
      }

      return true as const;
    })
    .catch((error: unknown) => {
      const message = error instanceof Error ? error.message : 'An unexpected error occurred while validating the file.';
      console.error('[isValidCsv] Validation failed:', error);
      throw new Error(message);
    });
};

export const validateCSV = (requiredHeaders: string[], file: File): Promise<true> => {
  return getCsvHeaders(file)
    .then(async (csvHeaders) => {
      checkMissingHeaders(csvHeaders, requiredHeaders, file.name);

      const csvData = await getCsvData(file);
      checkHasData(csvData, file);

      return true as const;
    })
    .catch((error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to validate the CSV file. Please check the format and try again.';
      console.error('[validateCSV] Validation failed:', error);
      throw new Error(message);
    });
};

export const downloadFile = (blob: Blob, fileName: string): void => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const downloadCSVHeaders = (fileName: string, headers: string[], data?: any): void => {
  const csv = Papa.unparse({ fields: headers, data: data ?? [] });
  downloadFile(new Blob([csv]), fileName);
};

export const downloadCSV = (csvHeader: string[], csvBody: string[][], fileName: string): void => {
  const csv = Papa.unparse({ fields: csvHeader, data: csvBody });
  downloadFile(new Blob([csv]), fileName);
};

export const downloadCSVFromJson = (data: JSON[], fileName: string): void => {
  const csv = Papa.unparse(data);
  downloadFile(new Blob([csv]), fileName);
};

export const getCSVDataAsJsonArray = (csvFile: File): Promise<Record<string, string>[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete(result) {
        if (result.errors.length) {
          console.warn(`Parse warnings in "${csvFile.name}":`, formatParseErrors(result.errors));
        }

        const filteredData = (result.data as Record<string, string>[]).filter((row) =>
          Object.values(row).some((value) => value !== '')
        );

        if (filteredData.length === 0) {
          reject(
            new Error(
              `"${csvFile.name}" has no data rows. Please add at least one data row and try again.`
            )
          );
          return;
        }

        resolve(filteredData);
      },
      error(error) {
        console.error(`[getCSVDataAsJsonArray] Error parsing "${csvFile.name}":`, error);
        reject(new Error(`Failed to read "${csvFile.name}": ${error.message}`));
      },
    });
  });
};

export const convertToCSV = (data: Record<string, string>[]): string => {
  if (!data.length) return '';
  const header = Object.keys(data[0]).join(',') + '\n';
  const rows = data.map((row) => Object.values(row).join(',')).join('\n');
  return header + rows;
};
